import type { PhysicsStore } from "../core/store.js";
import { computeNormalForce } from "../logic/mechanics-model.js";
import { computePinholeImageHeight, computeShadowHeight } from "../logic/optics-model.js";

export type PhysicsActionId =
  | "pull_uniform"
  | "add_weight"
  | "select_surface"
  | "select_orientation"
  | "move_candle"
  | "move_screen"
  | "move_light"
  | "move_shadow_screen";

export interface ActionPayload {
  surfaceId?: string;
  orientation?: "flat" | "vertical";
  /** 拖拽提交的数值。move_candle/move_screen/move_light/move_shadow_screen 是距离（cm），
   *  pull_uniform 是匀速滑动时测力计的稳定示数（N）。 */
  value?: number;
}

export interface ActionResult {
  actionId: PhysicsActionId;
  /** 本次动作后受影响的 variable id 列表，供调用方决定发哪些 adjustment/measurement 事件。 */
  changedVariables: string[];
}

const MAX_WEIGHT_COUNT = 3;

function requireValue(actionId: PhysicsActionId, payload: ActionPayload): number {
  if (payload.value == null) {
    throw new Error(`[action-controller] ${actionId} 缺少 value`);
  }
  return payload.value;
}

/**
 * 执行力学实验的具名操作，更新 PhysicsStore 的变量/关系。
 * 对齐 tech/108 §4.4：物理（力学）的核心交互是按钮式操作，不是像素级拖拽。
 *
 * 不在这里记账 `store.recordAction`——动作要按 (stepId, actionId) 记账（tech/108 §7），
 * 而这层不知道"当前 Step 是谁"，记账职责交给调用方 mission-engine。
 */
export function performAction(store: PhysicsStore, actionId: PhysicsActionId, payload: ActionPayload = {}): ActionResult {
  switch (actionId) {
    case "add_weight": {
      const current = Number(store.variables.get("weightCount")?.value ?? 0);
      const next = Math.min(current + 1, MAX_WEIGHT_COUNT);
      store.variables.set("weightCount", next);
      return { actionId, changedVariables: ["weightCount"] };
    }
    case "select_surface": {
      if (!payload.surfaceId) {
        throw new Error("[action-controller] select_surface 缺少 surfaceId");
      }
      store.variables.set("surface", payload.surfaceId);
      store.relations.setRelation(`block-on-${payload.surfaceId}`, "placedOn", "block", `surface:${payload.surfaceId}`);
      return { actionId, changedVariables: ["surface"] };
    }
    case "select_orientation": {
      if (!payload.orientation) {
        throw new Error("[action-controller] select_orientation 缺少 orientation");
      }
      store.variables.set("orientation", payload.orientation);
      return { actionId, changedVariables: ["orientation"] };
    }
    case "pull_uniform": {
      const weightCount = Number(store.variables.get("weightCount")?.value ?? 0);
      const normalForce = computeNormalForce(weightCount);
      // 读数必须来自学生实际匀速拉动时测力计的稳定示数（payload.value），
      // 而不是直接套 f=μN 的理论值——否则"记录的数据"和"实验台上拉出的示数"会对不上。
      const friction = requireValue(actionId, payload);
      store.variables.set("normalForce", normalForce);
      store.variables.set("frictionReading", friction);
      return { actionId, changedVariables: ["normalForce", "frictionReading"] };
    }
    case "move_candle": {
      const objectDistance = requireValue(actionId, payload);
      const screenDistance = Number(store.variables.get("screenDistance")?.value ?? 30);
      store.variables.set("objectDistance", objectDistance);
      store.variables.set("imageHeight", computePinholeImageHeight(objectDistance, screenDistance));
      return { actionId, changedVariables: ["objectDistance", "imageHeight"] };
    }
    case "move_screen": {
      const screenDistance = requireValue(actionId, payload);
      const objectDistance = Number(store.variables.get("objectDistance")?.value ?? 30);
      store.variables.set("screenDistance", screenDistance);
      store.variables.set("imageHeight", computePinholeImageHeight(objectDistance, screenDistance));
      return { actionId, changedVariables: ["screenDistance", "imageHeight"] };
    }
    case "move_light": {
      const lightObjectDistance = requireValue(actionId, payload);
      const objectScreenDistance = Number(store.variables.get("objectScreenDistance")?.value ?? 30);
      store.variables.set("lightObjectDistance", lightObjectDistance);
      store.variables.set("shadowHeight", computeShadowHeight(lightObjectDistance, objectScreenDistance));
      return { actionId, changedVariables: ["lightObjectDistance", "shadowHeight"] };
    }
    case "move_shadow_screen": {
      const objectScreenDistance = requireValue(actionId, payload);
      const lightObjectDistance = Number(store.variables.get("lightObjectDistance")?.value ?? 30);
      store.variables.set("objectScreenDistance", objectScreenDistance);
      store.variables.set("shadowHeight", computeShadowHeight(lightObjectDistance, objectScreenDistance));
      return { actionId, changedVariables: ["objectScreenDistance", "shadowHeight"] };
    }
    default: {
      const exhaustive: never = actionId;
      throw new Error(`[action-controller] 未知动作：${exhaustive}`);
    }
  }
}

/**
 * 把 act 步骤会改动的变量复位到初始默认值——`returnToAct()` 回环回操作台时调用，保证"操作台"的
 * 视觉状态与已清空的门控动作计数同步。默认值和 mission.json `variables` 里定义的初始值一致
 * （surface=wood / orientation=flat / weightCount=0 / normalForce & frictionReading=null）。
 *
 * 否则 `clearActionsForStep` 清了动作计数，但砝码还画在木块上、材质还选中、测力计还留着上次读数，
 * 学生会困惑"明明已经加了砝码怎么还差一步"（真机反馈"返回操作台后卡在还差一步"的确凿根因）。
 */
export function resetActState(store: PhysicsStore, actions: readonly string[]): void {
  if (actions.includes("add_weight")) store.variables.set("weightCount", 0);
  if (actions.includes("select_surface")) store.variables.set("surface", "wood");
  if (actions.includes("select_orientation")) store.variables.set("orientation", "flat");
  if (actions.includes("pull_uniform")) {
    store.variables.set("normalForce", null);
    store.variables.set("frictionReading", null);
  }
  if (actions.includes("move_candle")) store.variables.set("objectDistance", 30);
  if (actions.includes("move_screen")) store.variables.set("screenDistance", 30);
  if (actions.includes("move_light")) store.variables.set("lightObjectDistance", 30);
  if (actions.includes("move_shadow_screen")) store.variables.set("objectScreenDistance", 30);
  if (actions.some((id) => id === "move_candle" || id === "move_screen")) store.variables.set("imageHeight", null);
  if (actions.some((id) => id === "move_light" || id === "move_shadow_screen")) store.variables.set("shadowHeight", null);
}
